import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Send } from "lucide-react";
import { api } from "../../lib/api";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import Placeholder from "../Placeholder";

const TITLES = {
  users: "إدارة المستخدمين", broadcast: "إرسال إشعار", categories: "القوائم",
  specializations: "تخصصات المتاجر", disputes: "فض النزاعات", withdrawals: "السحوبات",
  interests: "الاهتمامات", "payment-methods": "طرق الدفع", subscription: "اشتراك البائعين",
  "driver-subscription": "اشتراك السائقين", "promo-packages": "باقات التمويل",
  "driver-applications": "طلبات السائقين", cities: "المدن", "delivery-settings": "إعدادات التوصيل",
  chats: "مراقبة المحادثات", followers: "المتابعين", "platform-social": "روابط التواصل",
  "app-version": "اسم الإصدار",
};

export default function AdminSection() {
  const { section } = useParams();
  if (section === "users") return <AdminUsers />;
  if (section === "broadcast") return <AdminBroadcast />;
  if (section === "app-version") return <AppVersion />;
  return <Placeholder title={TITLES[section] || "لوحة المالك"} />;
}

function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api("/admin/users").then(setUsers).catch(() => setUsers([])); }, []);
  const filtered = useMemo(() => (users || []).filter((u) => !q || (u.name || "").includes(q) || (u.email || "").includes(q) || (u.phone || "").includes(q)), [users, q]);
  if (!users) return <Loader />;
  return (
    <div className="animate-fade-in-up max-w-3xl mx-auto">
      <PageHeader title="إدارة المستخدمين" subtitle={`${users.length} مستخدم`} backTo="/admin" />
      <div className="flex items-center gap-2 bg-white rounded-2xl px-4 h-12 border border-[#E8EAF2] mb-4">
        <Search size={18} className="text-[#9CA3AF]" />
        <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو البريد أو الهاتف" className="flex-1 outline-none bg-transparent" />
      </div>
      <div className="space-y-2">
        {filtered.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-[#E8EAF2]">
            <div className="w-11 h-11 rounded-xl bg-[#7C5CFF] text-white flex items-center justify-center font-black">{(u.name || "?")[0]}</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#161629] truncate">{u.name || "—"}</p>
              <p className="text-xs text-[#6B7280] truncate">{u.email || u.phone || ""}</p>
            </div>
            <span className="text-xs font-black px-2 py-1 rounded-lg bg-[#F0F2FA] text-[#6B7280]">{u.is_admin ? "أدمن" : u.role || "buyer"}</span>
            {u.banned && <span className="text-xs font-black px-2 py-1 rounded-lg bg-[#FFE5EA] text-[#FF3D5A]">محظور</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AppVersion() {
  const [version, setVersion] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/app-settings").then((r) => { setVersion(r?.version_name || "1.0"); setLoaded(true); }).catch(() => setLoaded(true)); }, []);
  const save = async () => {
    if (!version.trim()) return toast.error("أدخل اسم الإصدار");
    setBusy(true);
    try { const r = await api("/admin/app-settings", { method: "PUT", body: JSON.stringify({ version_name: version.trim() }) }); setVersion(r.version_name); toast.success("تم حفظ اسم الإصدار"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!loaded) return <Loader />;
  return (
    <div className="animate-fade-in-up max-w-lg mx-auto">
      <PageHeader title="اسم الإصدار" backTo="/admin" />
      <p className="text-sm text-[#6B7280] mb-3 bg-[#FFF7E0] border border-[#FFB627] rounded-xl p-3">النص الذي تكتبه هنا يظهر داخل التطبيق (الإعدادات والملف الشخصي) على الويب والجوال بصيغة «الإصدار …».</p>
      <p className="text-sm font-bold mb-1.5">اسم / رقم الإصدار</p>
      <input data-testid="app-version-input" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="مثال: 1.2.0 أو نسخة تجريبية" className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />
      <p className="text-xs text-[#6B7280] mt-2">معاينة: <span className="font-black text-[#161629]">نطاق للتسوق • الإصدار {version || "…"}</span></p>
      <button data-testid="app-version-save" disabled={busy} onClick={save} className="w-full h-13 py-4 rounded-2xl bg-[#FF3D5A] text-white font-black mt-5 disabled:opacity-60">{busy ? "..." : "حفظ"}</button>
    </div>
  );
}

function AdminBroadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!title.trim() || !body.trim()) return toast.error("العنوان والنص مطلوبان");
    setBusy(true);
    try { await api("/admin/broadcast", { method: "POST", body: JSON.stringify({ title: title.trim(), body: body.trim(), target }) }); toast.success("تم الإرسال"); setTitle(""); setBody(""); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="animate-fade-in-up max-w-lg mx-auto">
      <PageHeader title="إرسال إشعار للمستخدمين" backTo="/admin" />
      <p className="text-sm font-bold mt-2 mb-1.5">العنوان</p>
      <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />
      <p className="text-sm font-bold mt-4 mb-1.5">نص الرسالة</p>
      <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A] min-h-[100px]" />
      <p className="text-sm font-bold mt-4 mb-1.5">المستهدفون</p>
      <div className="flex gap-2 flex-wrap">
        {[["all", "الكل"], ["buyer", "المشترون"], ["seller", "البائعون"], ["driver", "السائقون"]].map(([k, l]) => (
          <button key={k} data-testid={`target-${k}`} onClick={() => setTarget(k)} className={`px-4 py-2 rounded-xl font-bold text-sm ${target === k ? "bg-[#FF3D5A] text-white" : "bg-white border border-[#E8EAF2]"}`}>{l}</button>
        ))}
      </div>
      <button data-testid="broadcast-send" disabled={busy} onClick={send} className="w-full flex items-center justify-center gap-2 h-13 py-4 rounded-2xl bg-[#FF3D5A] text-white font-black mt-6 disabled:opacity-60"><Send size={18} /> {busy ? "..." : "إرسال"}</button>
    </div>
  );
}
